import { MorphemeTilePresentationProvider } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.presentation";
import { MorphemeGlyphTileLink } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.link.glyphs";
import {
  type BatchedGraphemeTileData,
  getMorphemeGlyphTileGraphemeGlyphStyle,
  type GlyphStyle,
  MORPHEME_FRONT_BUTTON_PINYIN_DOTS_ROW_HEIGHT,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.glyphs";
import type { GraphemeGlyph } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.entities";
import { useMorphemePanelsHrefNavigator } from "@-/dev.improve.haniverse.lib.morpheme.page.navigator";
import filledArray from "@-/com.npmjs.filled-array";
import split from "@-/com.npmjs.just-split";
import { type ReactNode, useCallback, useMemo, useRef } from "react";
import { View } from "@-/com.npmjs.react-native";
import type { AppSelectionItem } from "@-/dev.improve.haniverse.routes.nav.app._context";
import arrayLength from "@-/com.npmjs.extra-array__length";
import { stringLength } from "@-/com.npmjs.string-length";
import {
  GRID_GAP_DEFAULT,
  MORPHEME_GLYPH_TILE_GRID_COLUMNS,
  normalizeGraphemeGlyph,
} from "#normalize-grapheme-glyph";

function isSingleGrapheme(morpheme: string): boolean {
  return stringLength(morpheme) === 1;
}

export type MorphemeGlyphTileGridViewProps = {
  /**
   * The grid's width when the caller knows it WITHOUT measuring (e.g. from `useNavContentWidth`
   * minus the page's own inset). With it, the tile size is arithmetic — `(width − gaps) / columns`
   * — and every single-grapheme tile is handed a precomputed glyph size, so it renders at its final
   * size on the first commit instead of measuring itself via `onLayout` and rendering twice (a
   * native round-trip per tile, ~150ms of JS across a hundred tiles in a dev build). Without it the
   * tiles measure themselves. Callers with virtualized rows can also supply `glyphStyle`.
   */
  availableWidth?: number;
  className?: string;
  columns?: number;
  gap?: number;
  glyphStyle?: GlyphStyle;
  graphemeList: readonly (string | GraphemeGlyph)[];
  href: string;
  isMissingMnemonic?: (morpheme: string) => boolean;
  onNavigate?: (morpheme: string) => void;
  renderTileOverlay?: (morpheme: string, index: number) => ReactNode;
  selectionItemsByMorpheme?: ReadonlyMap<string, readonly AppSelectionItem[]>;
  /** Show each grapheme pronunciation card's reading above its glyph. */
  showPrimaryPinyin?: boolean;
  tileClassName?: (morpheme: string, index: number) => string | undefined;
  /**
   * The lifted tile data (one stage + one pinyin slice per grapheme) every tile is handed as props.
   * A tile given both renders query-free; given neither it self-resolves, which is one stage query +
   * change subscription + pinyin read PER TILE — the storm this grid used to produce on every actor
   * write. `MorphemeGlyphTileList` lifts it in one batched read; a paged grid supplies
   * `usePagedGraphemeTileData`'s.
   */
  tileData: BatchedGraphemeTileData;
  /** An optional final cell, used for a collapsed collection's expansion button. */
  trailingCell?: ReactNode;
  uncolored?: boolean;
  view?: "mnemonics" | "info";
};

/**
 * The pure glyph grid: rows of link tiles, each fed its stage and slice from `tileData`. Owns no
 * data — see `MorphemeGlyphTileList` for the batched-read wrapper most callers want.
 *
 * Two things here exist so a grid of a hundred-plus tiles stays cheap to mount and to grow:
 *
 * - ONE navigator for the whole grid. Without an `onNavigate`, every tile mounts its own panels
 *   navigator (a `useMutation` trio plus router hooks) — the per-tile cost the tile's own docs
 *   warn about. The grid mounts a single one and hands every tile the same callback, which pushes
 *   the same destination the tile would have pushed itself.
 * - STABLE tile props. The tile is memoized (by the React Compiler), so when the reveal window
 *   grows (a paged grid appends a page) only the new tiles render — provided the mounted ones see
 *   identical props. The glyph objects are therefore cached per grapheme across list identities
 *   rather than re-normalized from the fresh array every render.
 */
function MorphemeGlyphTileGridViewContent({
  availableWidth,
  className,
  columns: columnCount = MORPHEME_GLYPH_TILE_GRID_COLUMNS,
  gap = GRID_GAP_DEFAULT,
  glyphStyle,
  graphemeList,
  href,
  isMissingMnemonic,
  onNavigate,
  renderTileOverlay,
  selectionItemsByMorpheme,
  showPrimaryPinyin,
  tileClassName,
  tileData: { getSlice, getStage },
  trailingCell,
  uncolored,
}: MorphemeGlyphTileGridViewProps & { onNavigate: (morpheme: string) => void }) {
  // Deterministic tile size: `flex-1` cells share the row width minus the gaps between them, and
  // the tile is square (`aspect-square`). Mirrors the zone math in `MorphemeGlyphTileContent`
  // (a grapheme tile reserves a dots row on both sides), like the Browse grids do. Only known
  // when the caller supplied the width — a measured width arrives after the first commit, by
  // which point the tiles have already measured themselves, so it would only cost a re-render.
  const graphemeGlyphStyle = useMemo<GlyphStyle | undefined>(() => {
    if (glyphStyle !== undefined) {
      return glyphStyle;
    }
    if (availableWidth === undefined) {
      return undefined;
    }
    const tileSize = Math.floor((availableWidth - gap * (columnCount - 1)) / columnCount);
    return getMorphemeGlyphTileGraphemeGlyphStyle({
      glyphZoneHeight: Math.max(1, tileSize - MORPHEME_FRONT_BUTTON_PINYIN_DOTS_ROW_HEIGHT * 2),
      glyphZoneWidth: Math.max(1, tileSize),
    });
  }, [availableWidth, columnCount, gap, glyphStyle]);

  // Glyph identity survives a new `graphemeList` array: a bare string maps to one cached glyph
  // object, and a caller-supplied `GraphemeGlyph` passes through as-is (its identity is the
  // caller's to keep stable).
  const normalizedGlyphByGraphemeRef = useRef(new Map<string, GraphemeGlyph>());
  const normalizedGraphemeList = useMemo(
    () =>
      graphemeList.map((entry) => {
        if (typeof entry !== "string") {
          return entry;
        }
        const cached = normalizedGlyphByGraphemeRef.current.get(entry);
        if (cached !== undefined) {
          return cached;
        }
        const normalized = normalizeGraphemeGlyph(entry);
        normalizedGlyphByGraphemeRef.current.set(entry, normalized);
        return normalized;
      }),
    [graphemeList],
  );
  const rows = useMemo(
    () =>
      split(
        trailingCell == null ? normalizedGraphemeList : [...normalizedGraphemeList, null],
        columnCount,
      ),
    [columnCount, normalizedGraphemeList, trailingCell],
  );

  return (
    <View className={className} style={{ gap }}>
      {rows.map((rowGraphemeList, rowIndex) => (
        <View className="flex flex-row" key={rowIndex} style={{ gap }}>
          {rowGraphemeList.map((glyph, colIndex) => (
            <View className="flex-1 items-center justify-center" key={`${rowIndex}-${colIndex}`}>
              {glyph === null ? (
                <View className="aspect-square w-full">{trailingCell}</View>
              ) : (
                <>
                  <MorphemeGlyphTileLink
                    className={tileClassName?.(glyph.original, rowIndex * columnCount + colIndex)}
                    // Word tiles size to their character count and keep measuring themselves.
                    glyphStyle={isSingleGrapheme(glyph.original) ? graphemeGlyphStyle : undefined}
                    href={href}
                    isMissingMnemonic={isMissingMnemonic?.(glyph.original)}
                    morpheme={glyph}
                    morphemeGlyphTileSlice={getSlice(
                      glyph.original,
                      rowIndex * columnCount + colIndex,
                    )}
                    onNavigate={onNavigate}
                    selectionItems={selectionItemsByMorpheme?.get(glyph.original)}
                    showPrimaryPinyin={showPrimaryPinyin}
                    stage={getStage(glyph.original, rowIndex * columnCount + colIndex)}
                    uncolored={uncolored}
                  />
                  {renderTileOverlay?.(glyph.original, rowIndex * columnCount + colIndex)}
                </>
              )}
            </View>
          ))}
          {filledArray(
            (placeholderIndex) => (
              <View
                className="flex-1 items-center justify-center"
                key={`placeholder-${rowIndex}-${placeholderIndex}`}
              >
                <View className="aspect-square w-full" />
              </View>
            ),
            columnCount - arrayLength(rowGraphemeList),
          )}
        </View>
      ))}
    </View>
  );
}

function SelfNavigatingMorphemeGlyphsGrid(props: MorphemeGlyphTileGridViewProps) {
  const { href, view = "mnemonics" } = props;
  const pushMutate = useMorphemePanelsHrefNavigator().push.mutate;
  const onNavigate = useCallback(
    (morpheme: string) => {
      pushMutate({ morpheme, prefix: { href, params: { view } } });
    },
    [href, pushMutate, view],
  );
  return <MorphemeGlyphTileGridViewContent {...props} onNavigate={onNavigate} />;
}

export function MorphemeGlyphTileGridView(props: MorphemeGlyphTileGridViewProps) {
  return (
    <MorphemeTilePresentationProvider>
      {props.onNavigate === undefined ? (
        <SelfNavigatingMorphemeGlyphsGrid {...props} />
      ) : (
        <MorphemeGlyphTileGridViewContent {...props} onNavigate={props.onNavigate} />
      )}
    </MorphemeTilePresentationProvider>
  );
}
